import { z } from 'zod';
import {
  ChangeOrderSchema,
  CO_NUMBER_MAX,
  CoLineSchema,
  coTotals,
  formatCoNumber,
  isEditable,
  parseCoNumber,
  type ChangeOrder,
  type CoLine,
  type CoPriceMode,
  type OwnerChangeOrder,
} from '../../domain/entities/change-order.ts';
import { MAX_MARKUP_MILLIONTHS } from '../../domain/entities/cost-code.ts';
import { MoneySchema, sumMoney, type Money } from '../../domain/money.ts';
import { newId } from '../../domain/id.ts';
import { expectUnchanged } from '../../ports/batch.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import { FunctionCallError, type FunctionsPort } from '../../ports/functions-port.ts';
import type { Unsubscribe } from '../../ports/query.ts';
import { NotFoundError } from '../../ports/repository.ts';

/**
 * Change orders from the client's side.
 *
 * Two things are not here and cannot be: the **number**, which a function
 * allocates in a transaction, and the **transitions**, which a function
 * decides. Everything else is a draft's own fields, and a draft is the only
 * thing a client may edit — once a price has been shown to a customer it
 * does not change underneath them.
 */
export class ChangeOrderError extends Error {
  readonly reason: 'invalid' | 'notDraft' | 'taken';

  constructor(reason: 'invalid' | 'notDraft' | 'taken', message: string) {
    super(message);
    this.name = 'ChangeOrderError';
    this.reason = reason;
  }
}

export interface ChangeOrderContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly jobId: string;
  readonly uid: string;
}

function repo(context: Pick<ChangeOrderContext, 'data' | 'companyId' | 'jobId'>) {
  return context.data.collection<ChangeOrder>(
    { companyId: context.companyId, jobId: context.jobId },
    'changeOrders',
  );
}

/**
 * Raise one.
 *
 * The id is generated here and held by the caller for the life of the
 * attempt, which is what makes a retry the same change order rather than a
 * second one with a second number — AGENTS.md §3, and the reason the
 * callable looks the document up before allocating.
 */
export async function createChangeOrder(
  functions: FunctionsPort,
  input: {
    readonly companyId: string;
    readonly jobId: string;
    readonly title: string;
    readonly description?: string;
    readonly approvalDeadline?: string;
    /** Supplied by a caller that is retrying; generated otherwise. */
    readonly changeOrderId?: string;
  },
): Promise<{ readonly changeOrderId: string; readonly number: string }> {
  const changeOrderId = input.changeOrderId ?? newId();
  return functions.call('createChangeOrder', {
    companyId: input.companyId,
    jobId: input.jobId,
    changeOrderId,
    title: input.title,
    ...(input.description === undefined ? {} : { description: input.description }),
    ...(input.approvalDeadline === undefined ? {} : { approvalDeadline: input.approvalDeadline }),
  });
}

/**
 * Renumber a draft by hand.
 *
 * `docs/features/05-change-orders.md` has the ID# as "auto, editable while
 * draft", and the reason somebody wants it is mundane: a job inherits a
 * numbering scheme from a contract, or the first change order of a phase
 * should be `0100`.
 *
 * A callable rather than a field on `editDraft`, and not because of
 * permissions — because of **uniqueness**. A security rule can read the
 * document being written and ask whether `number` changed; it cannot ask
 * whether any other change order in the job already holds the new one. Nor
 * can a client: it would have to read the collection, decide, and write,
 * and the decision would be stale by the time the write landed. So the
 * server does the read, the check and the write in one transaction, which
 * is the same reason `createChangeOrder` is a callable.
 *
 * The counter is the part that is easy to miss. If somebody types `0100`
 * while the counter is at 7, the counter will reach 100 eventually and
 * hand out a number that is already taken. The server pushes the counter
 * past any number typed by hand, so the sequence is left with a gap rather
 * than a future collision — and gaps are already normal here: voiding one
 * leaves a gap, and `docs/features/05-change-orders.md` says so.
 */
export async function renumberDraft(
  functions: FunctionsPort,
  input: {
    readonly companyId: string;
    readonly jobId: string;
    readonly changeOrderId: string;
    /** What somebody typed. One to four digits; padded before it is sent. */
    readonly number: string;
  },
): Promise<{ readonly number: string }> {
  const sequence = parseCoNumber(input.number);
  if (sequence === undefined) {
    throw new ChangeOrderError(
      'invalid',
      `A change order number is between 1 and ${CO_NUMBER_MAX}, digits only.`,
    );
  }
  try {
    return await functions.call('renumberChangeOrder', {
      companyId: input.companyId,
      jobId: input.jobId,
      changeOrderId: input.changeOrderId,
      number: formatCoNumber(sequence),
    });
  } catch (cause) {
    // Translated here rather than at the call site, so every screen that
    // renumbers says the same thing about a number that is taken. The code
    // arrives suffixed by the vendor (`functions/already-exists`), so the
    // test is on the end of it.
    if (cause instanceof FunctionCallError && cause.code.endsWith('already-exists')) {
      throw new ChangeOrderError(
        'taken',
        `Another change order on this job is already ${formatCoNumber(sequence)}.`,
      );
    }
    if (cause instanceof FunctionCallError && cause.code.endsWith('failed-precondition')) {
      throw new ChangeOrderError(
        'notDraft',
        'Only a draft can be renumbered. Pull it back to draft first.',
      );
    }
    throw cause;
  }
}

export const EditDraftInputSchema = z.object({
  title: z.string().trim().min(1, 'A change order needs a title').max(200).optional(),
  description: z.string().max(20_000).optional(),
  priceMode: z.custom<CoPriceMode>().optional(),
  flatFeePrice: MoneySchema.nullish(),
  showLinesToOwner: z.boolean().optional(),
  approvalDeadline: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullish(),
  tagIds: z.array(z.string().min(1)).optional(),
});
export type EditDraftInput = z.input<typeof EditDraftInputSchema>;

async function loadDraft(context: ChangeOrderContext, id: string): Promise<ChangeOrder> {
  const existing = await repo(context).get(id);
  if (!existing) throw new NotFoundError('changeOrders', id);
  if (!isEditable(existing.status)) {
    // Mirrored by the rules, which refuse the same write. Stated here so a
    // screen can disable the control rather than offer an action that fails.
    throw new ChangeOrderError(
      'notDraft',
      `A ${existing.status} change order cannot be edited. Pull it back to draft first.`,
    );
  }
  return existing;
}

async function write(
  context: ChangeOrderContext,
  existing: ChangeOrder,
  patch: Partial<ChangeOrder>,
): Promise<ChangeOrder> {
  const next = {
    ...patch,
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  };
  await context.data.commit([
    {
      kind: 'update',
      scope: { companyId: context.companyId, jobId: context.jobId },
      collection: 'changeOrders',
      id: existing.id,
      patch: next,
      expect: expectUnchanged(existing),
    },
  ]);
  return { ...existing, ...next };
}

export async function editDraft(
  context: ChangeOrderContext,
  id: string,
  input: EditDraftInput,
): Promise<ChangeOrder> {
  const parsed = EditDraftInputSchema.safeParse(input);
  if (!parsed.success) throw new ChangeOrderError('invalid', parsed.error.issues[0]!.message);

  const existing = await loadDraft(context, id);
  const patch: Partial<ChangeOrder> = {
    ...(parsed.data.title === undefined ? {} : { title: parsed.data.title }),
    ...(parsed.data.description === undefined ? {} : { description: parsed.data.description }),
    ...(parsed.data.priceMode === undefined ? {} : { priceMode: parsed.data.priceMode }),
    ...(parsed.data.flatFeePrice === undefined
      ? {}
      : { flatFeePrice: parsed.data.flatFeePrice ?? undefined }),
    ...(parsed.data.showLinesToOwner === undefined
      ? {}
      : { showLinesToOwner: parsed.data.showLinesToOwner }),
    ...(parsed.data.approvalDeadline === undefined
      ? {}
      : { approvalDeadline: parsed.data.approvalDeadline ?? undefined }),
    ...(parsed.data.tagIds === undefined ? {} : { tagIds: [...parsed.data.tagIds] }),
  };

  const checked = ChangeOrderSchema.safeParse({ ...existing, ...patch });
  if (!checked.success) throw new ChangeOrderError('invalid', checked.error.issues[0]!.message);

  return write(context, existing, patch);
}

export const SaveLineInputSchema = z.object({
  description: z.string().trim().min(1, 'A line needs a description').max(500),
  quantityThousandths: z.number().int().positive('A line needs a quantity'),
  unit: z.string().trim().max(20).optional(),
  unitCost: MoneySchema,
  markupMillionths: z.number().int().min(0).max(MAX_MARKUP_MILLIONTHS).optional(),
  costCodeId: z.string().min(1).optional(),
  showToOwner: z.boolean().optional(),
});
export type SaveLineInput = z.input<typeof SaveLineInputSchema>;

/** The most lines one change order may hold. */
export const MAX_CO_LINES = 200;

/**
 * Lines are one array field, written whole and guarded.
 *
 * The same shape as a to-do's checklist, for the same reasons: they are
 * always read with their change order, a reorder cannot leave two lines
 * claiming one position, and two people editing the price at once get a
 * detected conflict rather than a total that silently lost a line.
 */
export async function addLine(
  context: ChangeOrderContext,
  id: string,
  input: SaveLineInput,
): Promise<ChangeOrder> {
  const parsed = SaveLineInputSchema.safeParse(input);
  if (!parsed.success) throw new ChangeOrderError('invalid', parsed.error.issues[0]!.message);

  const existing = await loadDraft(context, id);
  if (existing.lines.length >= MAX_CO_LINES) {
    throw new ChangeOrderError(
      'invalid',
      `A change order holds at most ${MAX_CO_LINES} lines. Split it.`,
    );
  }

  const line: CoLine = {
    id: newId(),
    description: parsed.data.description,
    quantityThousandths: parsed.data.quantityThousandths,
    ...(parsed.data.unit ? { unit: parsed.data.unit } : {}),
    unitCost: parsed.data.unitCost,
    markupMillionths: parsed.data.markupMillionths ?? 0,
    ...(parsed.data.costCodeId ? { costCodeId: parsed.data.costCodeId } : {}),
    showToOwner: parsed.data.showToOwner ?? true,
  };
  const checked = CoLineSchema.safeParse(line);
  if (!checked.success) throw new ChangeOrderError('invalid', checked.error.issues[0]!.message);

  return write(context, existing, { lines: [...existing.lines, line] });
}

/**
 * Add several lines at once — what `Add from catalog` needs (P5-03b).
 *
 * **One write, and the cap checked before any of it.** A loop of
 * `addLine` would read and write once per pick, and would stop
 * mid-way on the hundredth line having already added ninety-nine —
 * leaving somebody to work out which of their picks landed. Here the
 * whole set is refused, nothing is written, and the message says how
 * many there is room for.
 *
 * The lines arrive already built, because the thing that builds them is
 * `catalogLinesFrom` and it is shared with the estimate. This validates
 * each one against the schema all the same: a shared builder is still an
 * input from this function's point of view.
 */
export async function addLines(
  context: ChangeOrderContext,
  id: string,
  lines: readonly SaveLineInput[],
): Promise<ChangeOrder> {
  if (lines.length === 0) return loadDraft(context, id);

  const existing = await loadDraft(context, id);
  const room = MAX_CO_LINES - existing.lines.length;
  if (lines.length > room) {
    throw new ChangeOrderError(
      'invalid',
      room <= 0
        ? `A change order holds at most ${MAX_CO_LINES} lines, and this one is full. Split it.`
        : `A change order holds at most ${MAX_CO_LINES} lines. There is room for ${room} more, and you chose ${lines.length}.`,
    );
  }

  const added: CoLine[] = [];
  for (const input of lines) {
    const parsed = SaveLineInputSchema.safeParse(input);
    if (!parsed.success) throw new ChangeOrderError('invalid', parsed.error.issues[0]!.message);
    const line: CoLine = {
      id: newId(),
      description: parsed.data.description,
      quantityThousandths: parsed.data.quantityThousandths,
      ...(parsed.data.unit ? { unit: parsed.data.unit } : {}),
      unitCost: parsed.data.unitCost,
      markupMillionths: parsed.data.markupMillionths ?? 0,
      ...(parsed.data.costCodeId ? { costCodeId: parsed.data.costCodeId } : {}),
      showToOwner: parsed.data.showToOwner ?? true,
    };
    const checked = CoLineSchema.safeParse(line);
    if (!checked.success) throw new ChangeOrderError('invalid', checked.error.issues[0]!.message);
    added.push(line);
  }

  return write(context, existing, { lines: [...existing.lines, ...added] });
}

export async function updateLine(
  context: ChangeOrderContext,
  id: string,
  lineId: string,
  input: SaveLineInput,
): Promise<ChangeOrder> {
  const parsed = SaveLineInputSchema.safeParse(input);
  if (!parsed.success) throw new ChangeOrderError('invalid', parsed.error.issues[0]!.message);

  const existing = await loadDraft(context, id);
  const current = existing.lines.find((line) => line.id === lineId);
  if (!current) throw new NotFoundError('changeOrders', `${id}/lines/${lineId}`);

  const next: CoLine = {
    ...current,
    description: parsed.data.description,
    quantityThousandths: parsed.data.quantityThousandths,
    unit: parsed.data.unit ?? current.unit,
    unitCost: parsed.data.unitCost,
    markupMillionths: parsed.data.markupMillionths ?? current.markupMillionths,
    costCodeId: parsed.data.costCodeId ?? current.costCodeId,
    showToOwner: parsed.data.showToOwner ?? current.showToOwner,
  };

  return write(context, existing, {
    lines: existing.lines.map((line) => (line.id === lineId ? next : line)),
  });
}

export async function removeLine(
  context: ChangeOrderContext,
  id: string,
  lineId: string,
): Promise<ChangeOrder> {
  const existing = await loadDraft(context, id);
  return write(context, existing, {
    lines: existing.lines.filter((line) => line.id !== lineId),
  });
}

/** Apply one markup to every line, which is what "20% on the lot" means. */
export async function setMarkupOnAllLines(
  context: ChangeOrderContext,
  id: string,
  markupMillionths: number,
): Promise<ChangeOrder> {
  if (!Number.isInteger(markupMillionths) || markupMillionths < 0) {
    throw new ChangeOrderError('invalid', 'A markup is a whole number of basis points, or zero');
  }
  const existing = await loadDraft(context, id);
  return write(context, existing, {
    lines: existing.lines.map((line) => ({ ...line, markupMillionths })),
  });
}

export type TransitionTo = 'released' | 'approved' | 'declined' | 'draft' | 'void';

/** Move one on. Server-side, because the transitions are the rules. */
export async function transitionChangeOrder(
  functions: FunctionsPort,
  input: {
    readonly companyId: string;
    readonly jobId: string;
    readonly changeOrderId: string;
    readonly to: TransitionTo;
    readonly reason?: string;
    readonly signaturePath?: string;
    readonly comment?: string;
    readonly disclaimer?: string;
    readonly onBehalfOf?: string;
  },
): Promise<{ readonly status: string }> {
  return functions.call('transitionChangeOrder', input);
}

// ---- reading -------------------------------------------------------------

/**
 * A job's change orders, newest number first.
 *
 * One query rather than two, because a change order has no `visibility`:
 * the rules gate it on being an internal member of the job, so there is no
 * private subset to ask for separately. The owner reads
 * `ownerChangeOrders` instead, which is a different collection entirely.
 */
export async function listChangeOrders(
  context: Pick<ChangeOrderContext, 'data' | 'companyId' | 'jobId'>,
): Promise<ChangeOrder[]> {
  const page = await repo(context).list({ orderBy: [{ field: 'number', direction: 'desc' }] });
  return [...page.items];
}

export function subscribeToChangeOrders(
  context: Pick<ChangeOrderContext, 'data' | 'companyId' | 'jobId'>,
  onChange: (orders: readonly ChangeOrder[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return repo(context).subscribe(
    { orderBy: [{ field: 'number', direction: 'desc' }] },
    (page) => onChange(page.items),
    onError,
  );
}

/** The owner's copies, which carry a price and no cost. */
export function subscribeToOwnerChangeOrders(
  context: Pick<ChangeOrderContext, 'data' | 'companyId' | 'jobId'>,
  onChange: (orders: readonly OwnerChangeOrder[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return context.data
    .collection<OwnerChangeOrder>(
      { companyId: context.companyId, jobId: context.jobId },
      'ownerChangeOrders',
    )
    .subscribe(
      { orderBy: [{ field: 'number', direction: 'desc' }] },
      (page) => onChange(page.items),
      onError,
    );
}

/** What the office needs to answer "where is everything" in one glance. */
export interface CoSummary {
  readonly total: number;
  readonly awaitingOwner: number;
  readonly approved: number;
  readonly approvedPrice: Money;
}

export function summariseChangeOrders(orders: readonly ChangeOrder[]): CoSummary {
  const approved = orders.filter((order) => order.status === 'approved');
  return {
    total: orders.length,
    awaitingOwner: orders.filter((order) => order.status === 'released').length,
    approved: approved.length,
    // Only approved ones count towards the revised price. A released change
    // order is a question, not money.
    //
    // Summed through `coTotals`, not through arithmetic of its own. An
    // earlier version repeated the quantity-and-markup calculation here and
    // that is exactly the duplication the entity's comment warns about: two
    // places computing one price is one of them eventually being wrong, and
    // the wrong one is discovered by a customer.
    approvedPrice: sumMoney(approved.map((order) => coTotals(order).price)),
  };
}
